// C:\Users\cornn\source\repos\purehealthnexus\src\components\nutrition\useFirestoreNutrition.jsx
// Enhanced version with dynamic date range aggregation

import { useEffect, useState, useCallback, useMemo } from "react";
import { db } from "../../firebase/firebaseConfig";
import {
  doc, getDoc, setDoc, addDoc, collection, serverTimestamp, runTransaction, Timestamp,
} from "firebase/firestore";
import { initialData } from "./nutritionData";

// --- tiny utils (no external deps) ---
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const todayKey = (d) => {
  const x = new Date(d); const y = new Date(Date.UTC(x.getFullYear(), x.getMonth(), x.getDate()));
  return y.toISOString().slice(0, 10); // YYYY-MM-DD
};
const changeHash = (obj) => {
  try { return btoa(unescape(encodeURIComponent(JSON.stringify(obj)))).slice(0, 48); } catch { return uid(); }
};

// Generate array of date keys between start and end (inclusive)
const getDateRangeKeys = (startDate, endDate) => {
  const keys = [];
  const current = new Date(startDate);
  const end = new Date(endDate);
  
  while (current <= end) {
    keys.push(todayKey(current));
    current.setDate(current.getDate() + 1);
  }
  
  return keys;
};

// Aggregate nutrition data across multiple days
const aggregateNutritionData = (dailySnapshots) => {
  if (!dailySnapshots || dailySnapshots.length === 0) return initialData;
  
  const aggregated = { ...initialData };
  const counts = {};
  
  // Sum all numeric values
  dailySnapshots.forEach(snapshot => {
    Object.keys(snapshot).forEach(nutrientId => {
      const nutrient = snapshot[nutrientId];
      if (!nutrient || typeof nutrient !== 'object') return;
      
      if (!aggregated[nutrientId]) aggregated[nutrientId] = {};
      if (!counts[nutrientId]) counts[nutrientId] = {};
      
      Object.keys(nutrient).forEach(key => {
        const value = nutrient[key];
        if (typeof value === 'number' && !isNaN(value)) {
          aggregated[nutrientId][key] = (aggregated[nutrientId][key] || 0) + value;
          counts[nutrientId][key] = (counts[nutrientId][key] || 0) + 1;
        } else if (typeof value === 'string') {
          // Keep string values from most recent snapshot
          aggregated[nutrientId][key] = value;
        }
      });
    });
  });
  
  // Calculate averages for todayDV and similar percentage fields
  Object.keys(aggregated).forEach(nutrientId => {
    Object.keys(aggregated[nutrientId]).forEach(key => {
      const count = counts[nutrientId]?.[key];
      if (count && count > 1) {
        // Average percentage-based fields
        if (key === 'todayDV' || key === 'avgDV' || key.includes('DV') || key.includes('Pct')) {
          aggregated[nutrientId][key] = Math.round(aggregated[nutrientId][key] / count);
        }
      }
    });
    
    // Store original avgDV if exists, or calculate from todayDV average
    if (aggregated[nutrientId].todayDV != null && dailySnapshots.length > 1) {
      aggregated[nutrientId].avgDV = aggregated[nutrientId].todayDV;
    }
  });
  
  return aggregated;
};

// Idempotency store: returns true if we acquired the key, false if duplicate
async function tryStartIdempotent({ userId, key }) {
  const ref = doc(db, "users", userId, "idempotency", key);
  const ok = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) return false;
    tx.set(ref, { createdAt: serverTimestamp() });
    return true;
  });
  return ok;
}
async function markCompleteIdempotent({ userId, key }) {
  const ref = doc(db, "users", userId, "idempotency", key);
  await setDoc(ref, { completedAt: serverTimestamp() }, { merge: true });
}

export function useFirestoreNutrition({
  userId,
  selectedDate,
  range,
  dateRange,
  vitaminOverride,
}) {
  const [nutritionData, setNutritionData] = useState(initialData);
  const [trial, setTrial] = useState({ active: false, daysLeft: null });
  const [loading, setLoading] = useState(true);

  // Memoize the date range keys to prevent unnecessary recalculations
  const dateKeys = useMemo(() => {
    if (range === "today") {
      return [todayKey(selectedDate)];
    }
    
    // For custom ranges, use the provided dateRange
    if (range === "custom" && dateRange?.start && dateRange?.end) {
      return getDateRangeKeys(dateRange.start, dateRange.end);
    }
    
    // For preset ranges (7d, 30d, etc.), calculate from selectedDate
    const daysMap = {
      "7d": 7,
      "30d": 30,
      "month": 30,
      "lastMonth": 30,
    };
    
    const days = daysMap[range] || 1;
    const endDate = new Date(selectedDate);
    const startDate = new Date(selectedDate);
    startDate.setDate(startDate.getDate() - (days - 1));
    
    return getDateRangeKeys(startDate, endDate);
  }, [selectedDate, range, dateRange]);

  // Load trial banner data
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) { 
        if (!cancelled) setTrial({ active: false, daysLeft: null }); 
        return; 
      }
      try {
        const snap = await getDoc(doc(db, "users", userId));
        if (!snap.exists()) { 
          if (!cancelled) setTrial({ active: false, daysLeft: null }); 
          return; 
        }
        const d = snap.data() || {};
        const endTs = d.endTrialPeriodDate instanceof Timestamp ? d.endTrialPeriodDate : null;
        const isActive = d.isTrialPeriodActive === true;
        if (endTs) {
          const end = endTs.toDate();
          const now = new Date();
          const ms = end.getTime() - now.getTime();
          const days = Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
          if (!cancelled) setTrial({ active: isActive && end > now, daysLeft: days });
        } else if (!cancelled) setTrial({ active: false, daysLeft: null });
      } catch {
        if (!cancelled) setTrial({ active: false, daysLeft: null });
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Load nutrition data for the selected date range
  useEffect(() => {
    let cancelled = false;
    
    (async () => {
      if (!userId) { 
        if (!cancelled) {
          const base = initialData;
          const withOverrides =
            vitaminOverride && typeof vitaminOverride === "object"
              ? { ...base, ...vitaminOverride }
              : base;
          setNutritionData(withOverrides);
          setLoading(false);
        }
        return; 
      }
      
      setLoading(true);
      
      try {
        // Fetch all snapshots for the date range
        const snapshots = await Promise.all(
          dateKeys.map(async (key) => {
            try {
              const snap = await getDoc(doc(db, "users", userId, "nutritionSnapshots", key));
              return snap.exists() ? snap.data() : null;
            } catch {
              return null;
            }
          })
        );
        
        // Filter out null snapshots and aggregate
        const validSnapshots = snapshots.filter((s) => s !== null);
        
        if (!cancelled) {
          if (validSnapshots.length > 0) {
            const aggregated = aggregateNutritionData(validSnapshots);
            const withOverrides =
              vitaminOverride && typeof vitaminOverride === "object"
                ? { ...aggregated, ...vitaminOverride }
                : aggregated;
            setNutritionData(withOverrides);
          } else {
            // No data found for this range, use initial data (plus any overrides)
            const base = initialData;
            const withOverrides =
              vitaminOverride && typeof vitaminOverride === "object"
                ? { ...base, ...vitaminOverride }
                : base;
            setNutritionData(withOverrides);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error("Error loading nutrition data:", error);
        if (!cancelled) {
          const base = initialData;
          const withOverrides =
            vitaminOverride && typeof vitaminOverride === "object"
              ? { ...base, ...vitaminOverride }
              : base;
          setNutritionData(withOverrides);
          setLoading(false);
        }
      }
    })();
    
    return () => { cancelled = true; };
  }, [userId, dateKeys, vitaminOverride]);

  // Publish a telemetry/event with guardrails
  const publishEvent = useCallback(async ({ type, payload }) => {
    if (!userId) return;
    const source = "nutrition:useFirestoreNutrition";
    const traceId = uid();
    const requestId = uid();
    const idempKey = `${type}:${todayKey(new Date())}:${changeHash(payload)}`;
    const envelope = {
      source, requestId, traceId,
      visited: [source], hopCount: 1,
      publishedAt: new Date().toISOString(),
      idempotencyKey: idempKey,
      changeHash: changeHash({ type, payload }),
      type, payload,
    };
    try {
      await addDoc(collection(db, "users", userId, "events"), envelope);
    } catch {
      // swallow telemetry failures to avoid UX impact
    }
  }, [userId]);

  // Action: log intake (idempotent)
  const logIntake = useCallback(async ({ nutrientId, amount, unit }) => {
    if (!userId) return;
    const dateKey = todayKey(new Date());
    const type = "nutrition.logIntake";
    const payload = { nutrientId, amount, unit, dateKey };
    const idempKey = `${type}:${dateKey}:${nutrientId}:${amount}:${unit}`;

    const started = await tryStartIdempotent({ userId, key: idempKey });
    if (!started) return;

    try {
      await addDoc(collection(db, "users", userId, "nutritionLogs"), {
        nutrientId, amount, unit, dateKey, createdAt: serverTimestamp(),
      });

      await setDoc(doc(db, "users", userId, "nutritionDaily", dateKey), 
        { touchedAt: serverTimestamp() }, 
        { merge: true });

      await markCompleteIdempotent({ userId, key: idempKey });
      await publishEvent({ type, payload });

      // Optimistic update
      setNutritionData((prev) => {
        const next = { ...prev };
        if (nutrientId === "vitaminD") {
          const curr = next.vitaminD || {};
          const goal = curr.goal || 800;
          const addedPct = Math.round((amount / goal) * 100);
          next.vitaminD = { 
            ...curr, 
            iu: (curr.iu || 0) + amount, 
            todayDV: Math.min(200, (curr.todayDV || 0) + addedPct) 
          };
        }
        return next;
      });
    } catch {
      await publishEvent({ type: `${type}.error`, payload: { ...payload, stage: "write" } });
    }
  }, [userId, publishEvent]);

  // Action: nudge %DV
  const nudgeDv = useCallback(async ({ nutrientId, deltaPct }) => {
    if (!userId) return;
    const dateKey = todayKey(new Date());
    const type = "nutrition.nudgeDv";
    const payload = { nutrientId, deltaPct, dateKey };
    const idempKey = `${type}:${dateKey}:${nutrientId}:${deltaPct}`;

    const started = await tryStartIdempotent({ userId, key: idempKey });
    if (!started) return;

    try {
      await addDoc(collection(db, "users", userId, "nutritionActions"), {
        kind: "dvNudge", nutrientId, deltaPct, dateKey, createdAt: serverTimestamp(),
      });
      await markCompleteIdempotent({ userId, key: idempKey });
      await publishEvent({ type, payload });

      setNutritionData((prev) => {
        const curr = prev[nutrientId] || {};
        return { 
          ...prev, 
          [nutrientId]: { 
            ...curr, 
            todayDV: Math.min(200, (curr.todayDV || 0) + (deltaPct || 0)) 
          } 
        };
      });
    } catch {
      await publishEvent({ type: `${type}.error`, payload: { ...payload, stage: "write" } });
    }
  }, [userId, publishEvent]);

  const openDetails = useCallback(async (payload) => {
    await publishEvent({ type: "ui.openDetails", payload });
  }, [publishEvent]);

  return {
    nutritionData,
    setNutritionData,
    trial,
    loading,
    actions: { logIntake, nudgeDv, openDetails },
  };
}
